import {
  ImageFragment,
  VideoContentFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';

export interface VideoSource {
  url: string;
  kind: 'crystallize' | 'external';
  thumbnail?: ImageFragment;
  /**
   * The progressive MP4 rendition of an upload, when Crystallize provides one. The player streams
   * `url`, which is the HLS manifest whenever there is one; a consumer that needs a plain media
   * file, like the schema.org `contentUrl`, takes this rendition first.
   */
  fileUrl?: string;
}

/** Relative playlist urls have to be resolvable too, so parsing always runs against a base. */
const PARSE_BASE = 'https://video-source.invalid';

/** Signed CDN urls carry a query string, so the extension is read from the path alone. */
function hasExtension(playlist: string | null | undefined, extension: string): boolean {
  if (typeof playlist !== 'string') {
    return false;
  }

  try {
    return new URL(playlist, PARSE_BASE).pathname.toLowerCase().endsWith(extension);
  } catch {
    return false;
  }
}

/**
 * Crystallize returns several renditions per upload. HLS is preferred because it adapts to the
 * connection, MP4 is the progressive fallback. The index-based fallbacks keep parity with the
 * older tiles that hard-coded `playlists[1]`.
 */
function pickPlaylist(playlists: string[]): string | undefined {
  return (
    playlists.find((playlist) => hasExtension(playlist, '.m3u8')) ??
    playlists.find((playlist) => hasExtension(playlist, '.mp4')) ??
    playlists[1] ??
    playlists[0]
  );
}

/**
 * Resolves the playable source for a tile: a Crystallize upload always wins over the
 * externally configured URL (YouTube / Vimeo / direct file).
 */
export function resolveVideoSource(
  video: VideoContentFragment | null | undefined,
  externalUrl: string | null | undefined,
): VideoSource | undefined {
  const hostedVideo = video?.videos?.[0];
  const playlists = hostedVideo?.playlists ?? [];
  const hostedUrl = pickPlaylist(playlists);

  if (hostedUrl) {
    const fileUrl = playlists.find((playlist) => hasExtension(playlist, '.mp4'));

    return {
      url: hostedUrl,
      kind: 'crystallize',
      thumbnail: hostedVideo?.thumbnails?.[0] ?? undefined,
      ...(fileUrl ? { fileUrl } : {}),
    };
  }

  const trimmedExternalUrl = externalUrl?.trim();
  if (trimmedExternalUrl) {
    return { url: trimmedExternalUrl, kind: 'external' };
  }

  return undefined;
}
